import { useEffect } from 'react';

type Props = {
  accent: string;
  onClose: () => void;
};

const BULLETS = [
  'One platform replacing five — project management, ticketing, CRM, HR, time tracking.',
  'Thirteen specialist AI agents, one per department — operational colleagues, not chatbots.',
  'Real-time client visibility scoped to their own work; absolute cross-client isolation.',
  'Built on Google Cloud with end-to-end audit logs and enterprise-grade auth.',
  'Strategic alignment from company purpose down to individual development, baked in.',
  'Multi-model AI gateway — blend Claude, GPT, Gemini, Grok in one place with cost tracking.',
];

export function NexusAboutModal({ accent, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="nexus-about-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(8,8,14,.78)',
        backdropFilter: 'blur(8px)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 9100,
        animation: 'fadeIn .25s ease both',
        padding: '40px 24px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(100%, 720px)',
          background: 'var(--cream)',
          color: 'var(--ink)',
          borderRadius: 14,
          padding: '40px 48px 36px',
          boxShadow: '0 30px 90px rgba(0,0,0,.55)',
          position: 'relative',
          maxHeight: '100%',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 18,
            right: 18,
            width: 34,
            height: 34,
            borderRadius: 999,
            border: '1px solid rgba(14,14,14,.14)',
            background: 'transparent',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--ink)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
            <path
              d="M2 2 L12 12 M12 2 L2 12"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <p className="eyebrow" style={{ color: accent, marginBottom: 10 }}>
          About Nexus
        </p>
        <h2
          id="nexus-about-title"
          className="h2"
          style={{ fontSize: 30, lineHeight: 1.15, maxWidth: 560 }}
        >
          The operating system Jones + Palmer runs on — and where you see exactly what’s happening
          on your work, in real time.
        </h2>
        <p
          className="body"
          style={{ margin: '14px 0 28px', fontSize: 15, color: 'rgba(14,14,14,.7)', maxWidth: 560 }}
        >
          One platform. Live visibility on every project. No more “let me check and get back to
          you.”
        </p>

        <section style={{ marginBottom: 26 }}>
          <p className="mono" style={{ color: 'rgba(14,14,14,.5)', marginBottom: 12 }}>
            For our clients
          </p>
          <p
            className="body"
            style={{ margin: '0 0 14px', fontSize: 14.5, color: 'rgba(14,14,14,.82)' }}
          >
            Nexus is your single point of contact with J+P. Sign in once and you see every project
            we’re running for you, every ticket you’ve raised, every deliverable in flight — with
            live status, the named colleague handling it, and the timeline. When you raise a support
            request, you watch the SLA clock and know who’s on it. When a milestone moves, you find
            out the same hour we do.
          </p>
          <p className="body" style={{ margin: 0, fontSize: 14.5, color: 'rgba(14,14,14,.82)' }}>
            And when you have a question outside working hours, you don’t have to wait. Talk to one
            of our AI agents — Paloma for project questions, Chambers for legal and commercial
            counsel — and get a real answer drawn from your account data, signed by the persona so
            you know what you’re getting. The system you see on screen is the same system your
            account director uses. There’s no “client view” of softened numbers; you see real
            operational data, scoped to your work and your work only.
          </p>
        </section>

        <section style={{ marginBottom: 26 }}>
          <p className="mono" style={{ color: 'rgba(14,14,14,.5)', marginBottom: 12 }}>
            For J+P colleagues
          </p>
          <p className="body" style={{ margin: 0, fontSize: 14.5, color: 'rgba(14,14,14,.82)' }}>
            Nexus replaces a fragmented stack — project management, ticketing, time tracking, HR
            conversations, sales pipeline — with one platform where the data is properly linked and
            AI does the work nobody has time for. Thirteen specialist agents triage tickets, flag
            stale opportunities, monitor SLA breaches before they happen, and surface the
            development conversations managers need to have. The Golden Thread connects company
            purpose down to individual goals, so everyone knows what their work is laddering up to.
            And the AI gateway lets staff use Claude, ChatGPT, Gemini, and Grok in one place — with
            full cost attribution by project and client.
          </p>
        </section>

        <section>
          <p className="mono" style={{ color: 'rgba(14,14,14,.5)', marginBottom: 12 }}>
            At a glance
          </p>
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {BULLETS.map((b) => (
              <li
                key={b}
                style={{
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: 'rgba(14,14,14,.82)',
                  paddingLeft: 22,
                  position: 'relative',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 8,
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: accent,
                  }}
                />
                {b}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
